import type { StreamProvider } from '@vgtv/api-client';
import type { Asset } from '@schibsted-svp/svp-api-types';

import {
  getAssetFlightTimesStart,
  getAssetMetadata,
  getTimestamp,
  isLiveStream,
} from '../../../utils/player/asset';
import { getCategoryMetadataValue } from '../../../utils/player/category';

export const isAssetSponsored = (
  provider: StreamProvider | 'brandstudio',
  asset: Asset
) => {
  if (provider !== 'ab') {
    return false;
  }
  const CAMPAIGN_CATEGORY_ID = 214;

  const { sponsoredLinkUrl, sponsoredLinkText, sponsored } =
    getAssetMetadata(asset);

  const isSponsored =
    sponsored === 'true' ||
    getCategoryMetadataValue(asset.category, 'sponsored') === 'true';

  const hasSponsoredLink =
    sponsoredLinkText &&
    sponsoredLinkUrl &&
    asset.category.parentId === CAMPAIGN_CATEGORY_ID;

  return Boolean(isSponsored && hasSponsoredLink);
};

export function hasSponsorData(asset: Asset) {
  const { sponsoredLinkUrl, sponsoredLinkText, sponsored } =
    getAssetMetadata(asset);

  const isSponsored =
    sponsored === 'true' ||
    getCategoryMetadataValue(asset.category, 'sponsored') === 'true';

  const hasSponsoredLink = Boolean(sponsoredLinkText && sponsoredLinkUrl);

  return isSponsored || hasSponsoredLink;
}

export function hasEditorialLink(asset: Asset) {
  const { editorialLinkText, editorialLinkUrl } = getAssetMetadata(asset);
  return Boolean(editorialLinkText && editorialLinkUrl);
}

export const isEpisode = (asset: Asset) => {
  const { category, series } = asset;
  const { seasonNumber, episodeNumber } = series || {};

  return Boolean(category?.isSeries && seasonNumber && episodeNumber);
};

export const isTtsAsset = (asset: Asset | undefined): boolean => {
  return !!asset?.additional?.metadata?.tts_articleId;
};

export const getLiveAssetFlightTimesStart = (
  asset: Asset,
  useMs: boolean
): number | undefined => {
  if (!isLiveStream(asset)) {
    return undefined;
  }

  const flightTimesStart = getAssetFlightTimesStart(asset, useMs);
  const { eventStartTime, studioStartTime } = getAssetMetadata(asset);

  if (studioStartTime) {
    return getTimestamp(parseInt(studioStartTime, 10), useMs);
  }

  if (eventStartTime) {
    return getTimestamp(parseInt(eventStartTime, 10), useMs);
  }

  return flightTimesStart;
};

export const getRealAssetFlightTimesStart = (asset: Asset, useMs: boolean) => {
  if (isLiveStream(asset)) {
    return getLiveAssetFlightTimesStart(asset, useMs);
  }

  return Math.max(
    // if flight times start is less than publication date, it was automatically set to creation date after upload
    getAssetFlightTimesStart(asset, useMs) || 0,
    getTimestamp(asset.published, useMs) || 0
  );
};

export const isCurrentlyLive = (asset: Asset): boolean => {
  const startTimeInMilliseconds = getLiveAssetFlightTimesStart(asset, true);

  if (!startTimeInMilliseconds) {
    return false;
  }

  const nowUnixTimestamp = Date.now();

  return nowUnixTimestamp >= startTimeInMilliseconds;
};

const LONGFORM_DURATION_IN_SECONDS = 5 * 60;

export const isLongform = (duration: number) => {
  return duration >= LONGFORM_DURATION_IN_SECONDS;
};
